import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Facebook } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  "Score Education & Coaching",
  "Credit Analysis & Action Plan",
  "Business Credit & Funding Guidance",
  "Not sure yet — advise me",
];

const inputCls =
  "bg-black/50 border-white/20 text-white placeholder:text-neutral-600 rounded-none h-12 focus-visible:ring-gold focus-visible:ring-offset-0 focus-visible:border-gold/60";

const BookingForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", preferred_date: "", preferred_time: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", service: "", preferred_date: "", preferred_time: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Full name</Label>
          <Input required value={form.name} onChange={set("name")} placeholder="Jordan Ellis" className={inputCls} data-testid="booking-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="booking-email-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Phone</Label>
          <Input required value={form.phone} onChange={set("phone")} placeholder="(555) 010-2244" className={inputCls} data-testid="booking-phone-input" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Service</Label>
          <Select value={form.service} onValueChange={set("service")} required>
            <SelectTrigger className={`${inputCls} font-sans`} data-testid="booking-service-select">
              <SelectValue placeholder="Choose a focus" />
            </SelectTrigger>
            <SelectContent className="bg-ink-elevated border-white/10 text-white rounded-none">
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-gold focus:text-black" data-testid={`booking-service-option-${s.split(" ")[0].toLowerCase()}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Preferred date</Label>
          <Input required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={`${inputCls} [color-scheme:dark]`} data-testid="booking-date-input" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Preferred time</Label>
          <Input required type="time" value={form.preferred_time} onChange={set("preferred_time")} className={`${inputCls} [color-scheme:dark]`} data-testid="booking-time-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Anything we should know? (optional)</Label>
        <Textarea value={form.notes} onChange={set("notes")} placeholder="Goals, timeline, questions..." className={`${inputCls} min-h-[100px]`} data-testid="booking-notes-input" />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="group w-full flex items-center justify-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
        data-testid="booking-submit-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request My Free Consultation <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
      </button>
    </form>
  );
};

const EnquiryForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Full name</Label>
          <Input required value={form.name} onChange={set("name")} placeholder="Jordan Ellis" className={inputCls} data-testid="enquiry-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Email</Label>
          <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="enquiry-email-input" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Topic (optional)</Label>
        <Select value={form.service} onValueChange={set("service")}>
          <SelectTrigger className={`${inputCls} font-sans`} data-testid="enquiry-service-select">
            <SelectValue placeholder="What's this about?" />
          </SelectTrigger>
          <SelectContent className="bg-ink-elevated border-white/10 text-white rounded-none">
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s} className="focus:bg-gold focus:text-black" data-testid={`enquiry-service-option-${s.split(" ")[0].toLowerCase()}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Message</Label>
        <Textarea required value={form.message} onChange={set("message")} placeholder="Tell us where you are and where you want to be..." className={`${inputCls} min-h-[140px]`} data-testid="enquiry-message-input" />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="group w-full flex items-center justify-center gap-3 border border-gold text-gold text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-gold hover:text-black transition-colors duration-300 disabled:opacity-60"
        data-testid="enquiry-submit-button"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" /></>}
      </button>
    </form>
  );
};

const ContactSection = () => (
  <section id="contact" className="relative py-28 lg:py-40 bg-ink-base overflow-hidden" data-testid="contact-section">
    <div className="absolute inset-0 opacity-[0.07]">
      <img
        src="https://images.pexels.com/photos/31650383/pexels-photo-31650383.jpeg"
        alt=""
        className="w-full h-full object-cover"
      />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
      <div className="lg:col-span-5">
        <SectionHeading
          overline="Begin Your Ascent"
          title={<>The first step is a<br /><span className="italic text-gold">conversation.</span></>}
          copy="Book a free, no-pressure consultation — or simply write to us. We'll review where you stand, outline what's possible, and tell you honestly whether we're the right guide."
          testId="contact-heading"
        />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mt-12 space-y-6"
        >
          {[
            ["Response time", "Within one business day"],
            ["Consultation", "30 minutes, free, zero obligation"],
            ["Approach", "Education first — always"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-4 border-b border-white/10 pb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 w-32 shrink-0">{k}</span>
              <span className="text-sm text-neutral-200 font-light">{v}</span>
            </div>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="mt-10 flex items-center gap-4"
        >
          <a
            href="https://www.facebook.com/share/1KAxAG7G3j/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Elevated Apex Co. on Facebook"
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-neutral-400 hover:border-gold hover:text-gold transition-colors duration-300"
            data-testid="contact-facebook-link"
          >
            <Facebook className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Find us on Facebook</p>
            <p className="text-xs text-neutral-600 font-light mt-0.5">Tips, education, and client wins</p>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        className="lg:col-span-7"
      >
        <div className="bg-ink-surface/80 backdrop-blur-2xl border border-white/10 p-8 lg:p-12">
          <Tabs defaultValue="booking">
            <TabsList className="bg-black/50 border border-white/10 rounded-none p-1 h-auto w-full grid grid-cols-2" data-testid="contact-tabs">
              <TabsTrigger
                value="booking"
                className="rounded-none font-mono text-[11px] uppercase tracking-[0.2em] py-3 data-[state=active]:bg-gold data-[state=active]:text-black data-[state=active]:shadow-none"
                data-testid="booking-tab-trigger"
              >
                Free Consultation
              </TabsTrigger>
              <TabsTrigger
                value="enquiry"
                className="rounded-none font-mono text-[11px] uppercase tracking-[0.2em] py-3 data-[state=active]:bg-gold data-[state=active]:text-black data-[state=active]:shadow-none"
                data-testid="enquiry-tab-trigger"
              >
                Send a Message
              </TabsTrigger>
            </TabsList>
            <TabsContent value="booking" className="mt-8">
              <BookingForm />
            </TabsContent>
            <TabsContent value="enquiry" className="mt-8">
              <EnquiryForm />
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    </div>
  </section>
);

export default ContactSection;
